import { useState, useMemo } from "react";
import { Typography, Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MainDialogBox from "../components/Dialog/MainDialogBox";
import DataGridSkeleton from "../components/DataGridSkeleton";
import CustomPagination from "./CustomPagination";
import CustomDivider from "./CustomDivider";
import SelectBoxField from "../components/formfields/SelectBoxField";
import { useRegisterStudentCoursesList } from "../../assets/api/useAdminApi";

export const useStudentColumnsWithDialog = () => {

  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [filter, setFilter] = useState({});
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const { data: CourseList, isLoading: isCourseListLoading } = useRegisterStudentCoursesList({
    ...filter,
    student_id: selectedRow?.id,
  });

  const courseRows = useMemo(() => {
    return CourseList?.data?.map((item, index) => ({
      ...item,
      id: index + 1,
    })) || [];
  }, [CourseList]);

  const CourseOptions = useMemo(() => {
    const map = new Map();
    CourseList?.data?.forEach(item => !map.has(item.title) && map.set(item.title, item));
    return Array.from(map.values()).map(item => ({
      id: item.course_id,
      value: item.title,
      label: item.title,
    }));
  }, [CourseList]);

  const ApartmentOptions = useMemo(() => {
    const map = new Map();
    CourseList?.data?.forEach(item => !map.has(item.apartment_name) && map.set(item.apartment_name, item));
    return Array.from(map.values()).map(item => ({
      id: item.apartment_name,
      value: item.apartment_name,
      label: item.apartment_name,
    }));
  }, [CourseList]);

  const handleChangeTitle = (value) =>
    setFilter(prev => ({ ...prev, course_id: value }));

  const handleClearTitle = () =>
    setFilter(({ course_id, ...rest }) => rest);

  const handleChangeApartment = (value) =>
    setFilter(prev => ({ ...prev, apartment_name: value }));

  const handleClearApartment = () =>
    setFilter(({ apartment_name, ...rest }) => rest);

  const viewStudentCourse = (row) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const studentColumns = [
    { field: "sr_no", headerName: "Sr No", width: 100, renderCell: (params) => <Typography>{params.value}</Typography> },
    { field: "firstname", headerName: "Parent's Name", width: 150, renderCell: (params) => <Typography>{params.value}</Typography> },
    { field: "lastname", headerName: "Student Name", width: 150, renderCell: (params) => <Typography>{params.value}</Typography> },
    { field: "username", headerName: "User Name", width: 150, renderCell: (params) => <Typography>{params.value}</Typography> },
    { field: "apartment_name", headerName: "Apartment Name", width: 250, renderCell: (params) => <Typography>{params.value}</Typography> },
    { field: "age", headerName: "Age", width: 100, renderCell: (params) => <Typography>{params.value}</Typography> },
    { field: "child_grade", headerName: "Grade", width: 100, renderCell: (params) => <Typography>{params.value}</Typography> },
    { field: "child_school", headerName: "School Name", width: 150, renderCell: (params) => <Typography>{params.value}</Typography> },
    { field: "email", headerName: "E-Mail Id", width: 200, renderCell: (params) => <Typography>{params.value}</Typography> },
    { field: "mobile", headerName: "Phone Number", width: 150, renderCell: (params) => <Typography>{params.value}</Typography> },
    { field: "subscription_status", headerName: "Status", width: 150, renderCell: (params) => <Typography>{params.value}</Typography> },
  ];

  const CourseColumns = [
    { field: "id", headerName: "Sr No", flex: 0.5, renderCell: (params) => <Typography>{params.value}</Typography> },
    { field: "title", headerName: "Course Name", width: 200, renderCell: (params) => <Typography fontSize={12}>{params.value}</Typography> },
    { field: "apartment_name", headerName: "Apartment Name", width: 150, renderCell: (params) => <Typography fontSize={12}>{params.value}</Typography> },
    { field: "location_name", headerName: "Location Name", flex: 1.5, renderCell: (params) => <Typography fontSize={12}>{params.value}</Typography> },
    { field: "start_date", headerName: "Start Date", flex: 1.5, renderCell: (params) => <Typography fontSize={12}>{params.value}</Typography> },
    { field: "end_date", headerName: "End Date", flex: 1.5, renderCell: (params) => <Typography fontSize={12}>{params.value}</Typography> },
  ];

  const dialogJSX = (
    <MainDialogBox open={open} handleClose={() => { setFilter(); handleClose(); }} width={"60%"}>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
        <Typography variant="h3" fontWeight="bold">{selectedRow.firstname} {selectedRow.lastname}</Typography>
        <Box sx={{ width: "40%" }}>
          <SelectBoxField
            label="Course Name"
            options={CourseOptions}
            isRequired
            onChange={handleChangeTitle}
            ClearFunc={handleClearTitle}
            isloading={isCourseListLoading}
          />
        </Box>
        <Box sx={{ width: "40%" }}>
          <SelectBoxField
            label="Apartment Name"
            options={ApartmentOptions}
            isRequired
            onChange={handleChangeApartment}
            ClearFunc={handleClearApartment}
            isloading={isCourseListLoading}
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: "10px" }}>
        <Typography variant="body1" my={2}>Student ID: {selectedRow.id}</Typography>
        <Typography variant="body1" my={2}>Apartment: {selectedRow.apartment_name}</Typography>
      </Box>

      <CustomDivider />

      {isCourseListLoading ? (
        <DataGridSkeleton rowsNum={5} />
      ) : (
        <DataGrid
          rows={courseRows}
          columns={CourseColumns}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10]}
          slots={{
            pagination: () => (
              <CustomPagination
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                totalRows={courseRows.length}
                pageSize={paginationModel.pageSize}
              />
            ),
          }}
        />
      )}
    </MainDialogBox>
  );

  return { studentColumns, dialogJSX, viewStudentCourse };
};
